import React, { useState } from 'react';

import Alert from '../../styleguide/alert/Alert';
import Button from '../../styleguide/button/Button';
import Dialog from '../../styleguide/dialog/Dialog';
import InputField from '../../styleguide/input_field/InputField';
import Tag from '../../styleguide/tag/Tag';

const styles = require('./ClaudeIntegration.module.scss');

const ACTIVITY_PER_PAGE = 20;

type TabId = 'connector' | 'setup' | 'what-you-can-do' | 'activity';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'connector', label: 'Claude connector' },
  { id: 'setup', label: 'Setup' },
  { id: 'what-you-can-do', label: 'What you can do' },
  { id: 'activity', label: 'Activity' },
];

function initialTab(): TabId {
  const hash = window.location.hash.replace('#', '');
  const match = TABS.find(tab => tab.id === hash);
  return match ? match.id : 'connector';
}

const USE_CASES = [
  {
    title: 'Source talent for a job',
    description:
      'Claude looks up the job, pulls the top candidate matches and reads their profiles to explain who fits and why.',
    prompt:
      'Find talent for our new CTO / Technical Co-Founder job and tell me why each match is a fit.',
  },
  {
    title: 'Turn checks into routines',
    description:
      'In Claude Code, any of these can run on a schedule and only ping you when something needs attention.',
    prompt:
      'Every Monday morning, summarize new applications across our published jobs and send me a digest.',
  },
  {
    title: 'Check on an account',
    description:
      'Combines engagement metrics, recent NPS answers, subscriptions and billing into a single health check.',
    prompt: 'How is the Acme Software account doing? Check engagement, recent NPS and billing.',
  },
  {
    title: 'Review application flow',
    description:
      'Searches applications and screening interviews across your jobs to show where candidates are waiting.',
    prompt:
      'Summarize this week’s applications across our published jobs and flag any stuck in curation.',
  },
  {
    title: 'Dig into an application',
    description:
      'Pulls a single application’s screening, feedbacks and current state to explain where it stands.',
    prompt:
      'Where does the latest application to our Senior Backend Engineer job stand? Summarize the feedback so far.',
  },
];

export interface TokenData {
  createdAt: string;
  id: number;
  lastUsedAt: string | null;
  name: string;
  revokedAt: string | null;
}

export interface ToolCallData {
  createdAt: string;
  durationMs: number | null;
  id: number;
  status: 'success' | 'error' | 'unauthorized';
  toolName: string;
}

interface Props {
  mcpEndpoint: string;
  tokens: TokenData[];
  tokensPath: string;
  toolCalls: ToolCallData[];
}

function csrfToken(): string {
  const el = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;
  return el ? el.content : '';
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const STATUS_VARIANT: Record<ToolCallData['status'], 'success' | 'error' | 'warning'> = {
  success: 'success',
  error: 'error',
  unauthorized: 'warning',
};

function CopyButton({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      // Clipboard access denied — nothing sensible to do beyond not confirming.
    }
  };

  return (
    <Button
      context='neutral'
      type='secondary'
      size='S'
      onClick={() => {
        void copy();
      }}
      aria-label={label}
    >
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

export default function ClaudeIntegration(props: Props) {
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const [tokens, setTokens] = useState<TokenData[]>(props.tokens);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [generateOpen, setGenerateOpen] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [newSecret, setNewSecret] = useState<{ name: string; secret: string } | null>(null);

  const [tokenToRevoke, setTokenToRevoke] = useState<TokenData | null>(null);

  const [activityPage, setActivityPage] = useState(0);
  const activityPageCount = Math.ceil(props.toolCalls.length / ACTIVITY_PER_PAGE);
  const activityStart = activityPage * ACTIVITY_PER_PAGE;
  const pagedToolCalls = props.toolCalls.slice(activityStart, activityStart + ACTIVITY_PER_PAGE);

  const claudeCodeCommand =
    `claude mcp add landing-jobs --transport http ${props.mcpEndpoint} ` +
    '--header "Authorization: Bearer YOUR_TOKEN"';

  const closeGenerateForm = (): void => {
    setGenerateOpen(false);
    setNewTokenName('');
    setNameError(undefined);
  };

  const generateToken = async (): Promise<void> => {
    const name = newTokenName.trim();
    if (!name) {
      setNameError('Give the token a name, e.g. "Claude Code".');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(props.tokensPath, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({ name }),
      });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const data = await response.json();
      const { secret, ...token } = data.token;
      setTokens([token as TokenData, ...tokens]);
      setNewSecret({ name: token.name, secret });
      closeGenerateForm();
    } catch (_e) {
      setError('Could not generate the token — please try again.');
    } finally {
      setLoading(false);
    }
  };

  const revokeToken = async (token: TokenData): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${props.tokensPath}/${token.id}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
      });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const data = await response.json();
      setTokens(tokens.map(t => (t.id === token.id ? (data.token as TokenData) : t)));
    } catch (_e) {
      setError('Could not revoke the token — please try again.');
    } finally {
      setLoading(false);
      setTokenToRevoke(null);
    }
  };

  const activeTokens = tokens.filter(t => !t.revokedAt);

  const selectTab = (tab: TabId): void => {
    setActiveTab(tab);
    window.history.replaceState(null, '', `#${tab}`);
  };

  const onTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    const index = TABS.findIndex(tab => tab.id === activeTab);
    let next: TabId | undefined;
    if (event.key === 'ArrowRight') next = TABS[(index + 1) % TABS.length].id;
    if (event.key === 'ArrowLeft') next = TABS[(index + TABS.length - 1) % TABS.length].id;
    if (event.key === 'Home') next = TABS[0].id;
    if (event.key === 'End') next = TABS[TABS.length - 1].id;
    if (next) {
      event.preventDefault();
      selectTab(next);
      document.getElementById(`claude-tab-${next}`)?.focus();
    }
  };

  return (
    <div className={styles['ui-claude-integration']}>
      <header className={styles['ui-claude-integration__header']}>
        <h1>Claude Integration</h1>
        <p>
          Connect Claude to Landing.Jobs data. Every query runs with your backoffice permissions
          and is logged in the Activity tab.
        </p>
      </header>

      {error && <Alert variant='error' message={error} />}

      <div
        role='tablist'
        aria-label='Claude integration'
        className={styles['ui-claude-integration__tabs']}
      >
        {TABS.map(tab => (
          <button
            key={tab.id}
            id={`claude-tab-${tab.id}`}
            role='tab'
            type='button'
            aria-selected={activeTab === tab.id}
            aria-controls={`claude-panel-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
            className={styles['ui-claude-integration__tab']}
            onClick={() => selectTab(tab.id)}
            onKeyDown={onTabKeyDown}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        id='claude-panel-connector'
        role='tabpanel'
        aria-labelledby='claude-tab-connector'
        hidden={activeTab !== 'connector'}
        className={styles['ui-claude-integration__panel']}
      >
        <section
          aria-labelledby='claude-connector-heading'
          className={styles['ui-claude-integration__section']}
        >
          <h2 id='claude-connector-heading'>Connect Claude</h2>
          <p>
            The connector is already published to the Landing.Jobs Claude workspace. There is
            nothing to paste and no token to create.
          </p>
          <ol>
            <li>Open claude.ai (or Claude Desktop).</li>
            <li>
              Go to <strong>Settings → Connectors</strong>.
            </li>
            <li>
              Find <strong>Landing.Jobs Claude Connector</strong> and click{' '}
              <strong>Connect</strong>.
            </li>
            <li>Sign in to the backoffice and approve access.</li>
          </ol>
          <p>Personal tokens on the Setup tab are only needed for Claude Code or scripts.</p>
        </section>
      </div>

      <div
        id='claude-panel-setup'
        role='tabpanel'
        aria-labelledby='claude-tab-setup'
        hidden={activeTab !== 'setup'}
        className={styles['ui-claude-integration__panel']}
      >
        <section
          aria-labelledby='claude-endpoint-heading'
          className={styles['ui-claude-integration__section']}
        >
          <h2 id='claude-endpoint-heading'>MCP endpoint</h2>
          <div className={styles['ui-claude-integration__code-row']}>
            <code>{props.mcpEndpoint}</code>
            <CopyButton label='Copy MCP endpoint' value={props.mcpEndpoint} />
          </div>
        </section>

        <section
          aria-labelledby='claude-tokens-heading'
          className={styles['ui-claude-integration__section']}
        >
          <div className={styles['ui-claude-integration__section-header']}>
            <h2 id='claude-tokens-heading'>Access tokens</h2>
            {!generateOpen && (
              <Button
                context='neutral'
                type='primary'
                size='M'
                iconLeft='plus'
                onClick={() => setGenerateOpen(true)}
              >
                Generate token
              </Button>
            )}
          </div>

          {generateOpen && (
            <form
              className={styles['ui-claude-integration__generate-form']}
              onSubmit={e => {
                e.preventDefault();
                void generateToken();
              }}
            >
              <InputField
                name='token_name'
                label='Token name'
                hint="Where you'll use it, e.g. 'Claude Code'"
                error={nameError}
                value={newTokenName}
                required
                onChange={e => setNewTokenName(e.target.value)}
              />
              <div className={styles['ui-claude-integration__generate-actions']}>
                <Button
                  context='neutral'
                  type='primary'
                  size='M'
                  typeAttr='button'
                  spinner={loading}
                  disabled={loading}
                  onClick={() => void generateToken()}
                >
                  Generate
                </Button>
                <Button
                  context='neutral'
                  type='secondary'
                  size='M'
                  typeAttr='button'
                  onClick={closeGenerateForm}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {newSecret && (
            <div role='status' className={styles['ui-claude-integration__secret']}>
              <Alert
                variant='success'
                message={`Token "${newSecret.name}" created`}
                description="Copy it now — it won't be shown again."
              />
              <div className={styles['ui-claude-integration__code-row']}>
                <code>{newSecret.secret}</code>
                <CopyButton label='Copy access token' value={newSecret.secret} />
                <Button
                  context='neutral'
                  type='secondary'
                  size='S'
                  typeAttr='button'
                  aria-label='Hide access token'
                  onClick={() => setNewSecret(null)}
                >
                  Hide
                </Button>
              </div>
            </div>
          )}

          {tokens.length === 0 ? (
            <div className={styles['ui-claude-integration__empty']}>
              <p>No tokens yet.</p>
              <p>
                Generate a personal token, then add the endpoint to Claude using the instructions
                below. Tokens act as you: Claude only sees what you can already see in the
                backoffice.
              </p>
            </div>
          ) : (
            <ul className={styles['ui-claude-integration__token-list']}>
              {tokens.map(token => (
                <li key={token.id} className={styles['ui-claude-integration__token']}>
                  <div className={styles['ui-claude-integration__token-info']}>
                    <span className={styles['ui-claude-integration__token-name']}>
                      {token.name}
                    </span>
                    <span className={styles['ui-claude-integration__token-meta']}>
                      Created {formatDate(token.createdAt)} · Last used{' '}
                      {formatDate(token.lastUsedAt)}
                    </span>
                  </div>
                  {token.revokedAt ? (
                    <Tag variant='neutral'>Revoked</Tag>
                  ) : (
                    <div className={styles['ui-claude-integration__token-actions']}>
                      <Tag variant='success'>Active</Tag>
                      <Button
                        context='neutral'
                        type='secondary'
                        size='S'
                        iconLeft='trash'
                        disabled={loading}
                        onClick={() => setTokenToRevoke(token)}
                      >
                        Revoke
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section
          aria-labelledby='claude-setup-heading'
          className={styles['ui-claude-integration__section']}
        >
          <h2 id='claude-setup-heading'>Claude Code</h2>
          <p>
            Replace <code>YOUR_TOKEN</code> with a token generated above
            {activeTokens.length === 0 && ' (you need one first)'}. On claude.ai or Claude
            Desktop, use the Claude connector tab instead. No token is needed there.
          </p>
          <div className={styles['ui-claude-integration__code-row']}>
            <pre>
              <code>{claudeCodeCommand}</code>
            </pre>
            <CopyButton label='Copy Claude Code command' value={claudeCodeCommand} />
          </div>
        </section>
      </div>

      <div
        id='claude-panel-what-you-can-do'
        role='tabpanel'
        aria-labelledby='claude-tab-what-you-can-do'
        hidden={activeTab !== 'what-you-can-do'}
        className={styles['ui-claude-integration__panel']}
      >
        <p>
          Prompts to try once you&rsquo;re connected. Claude combines the read-only tools on this
          endpoint, such as job matches, profiles, engagement metrics and billing, and explains
          what it finds.
        </p>

        {USE_CASES.map((useCase, index) => (
          <section
            key={useCase.title}
            aria-labelledby={`claude-use-case-${index}`}
            className={styles['ui-claude-integration__section']}
          >
            <h2 id={`claude-use-case-${index}`}>{useCase.title}</h2>
            <p>{useCase.description}</p>
            <div className={styles['ui-claude-integration__code-row']}>
              <span className={styles['ui-claude-integration__prompt']}>
                &ldquo;{useCase.prompt}&rdquo;
              </span>
              <CopyButton label={`Copy prompt: ${useCase.title}`} value={useCase.prompt} />
            </div>
          </section>
        ))}
      </div>

      <div
        id='claude-panel-activity'
        role='tabpanel'
        aria-labelledby='claude-tab-activity'
        hidden={activeTab !== 'activity'}
        className={styles['ui-claude-integration__panel']}
      >
        <div className={styles['ui-claude-integration__section']}>
          {props.toolCalls.length === 0 ? (
            <div className={styles['ui-claude-integration__empty']}>
              <p>Nothing yet. Once Claude starts querying, your calls show up here.</p>
            </div>
          ) : (
            <>
              <div className={styles['ui-claude-integration__table-wrap']}>
                <table className={styles['ui-claude-integration__table']}>
                  <thead>
                    <tr>
                      <th scope='col'>Tool</th>
                      <th scope='col'>Status</th>
                      <th scope='col'>Duration</th>
                      <th scope='col'>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedToolCalls.map(call => (
                      <tr key={call.id}>
                        <td>
                          <code>{call.toolName}</code>
                        </td>
                        <td>
                          <Tag variant={STATUS_VARIANT[call.status]}>{call.status}</Tag>
                        </td>
                        <td>{call.durationMs == null ? '—' : `${call.durationMs} ms`}</td>
                        <td>{formatDate(call.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {activityPageCount > 1 && (
                <div className={styles['ui-claude-integration__pager']}>
                  <span className={styles['ui-claude-integration__pager-status']}>
                    {`${activityStart + 1}–${activityStart + pagedToolCalls.length} of ${
                      props.toolCalls.length
                    }`}
                  </span>
                  <div className={styles['ui-claude-integration__pager-actions']}>
                    <Button
                      context='neutral'
                      type='secondary'
                      size='S'
                      iconLeft='chevron-left'
                      disabled={activityPage === 0}
                      onClick={() => setActivityPage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      context='neutral'
                      type='secondary'
                      size='S'
                      iconRight='chevron-right'
                      disabled={activityPage >= activityPageCount - 1}
                      onClick={() => setActivityPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Dialog
        title='Revoke token?'
        context='neutral'
        isOpen={tokenToRevoke != null}
        confirmText={loading ? 'Revoking…' : 'Revoke'}
        cancelText='Keep it'
        onConfirm={() => {
          if (tokenToRevoke) void revokeToken(tokenToRevoke);
        }}
        onClose={() => setTokenToRevoke(null)}
        message={
          tokenToRevoke
            ? `"${tokenToRevoke.name}" will stop working immediately. Anything using it loses access. This cannot be undone.`
            : ''
        }
      />
    </div>
  );
}
